import type {
  ApAgingRow,
  ApAllocateInput,
  Page,
  Purchase,
  PurchaseDuplicate,
  PurchaseInput,
  PurchaseListItem,
  PurchaseSummary,
  PurchaseUpdate,
  SupplierPayment,
  SupplierPaymentInput,
  SupplierPaymentSummary,
  SupplierReturn,
  SupplierReturnInput,
  SupplierReturnListItem,
  SupplierReturnSummary,
  SupplierReturnUpdate,
  SupplierStatement,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api, type DownloadedFile } from '../../lib/api'
import type { Language } from '../../lib/format'
import { invalidateLedger } from '../accounting/queries'
import { inventoryKeys } from '../inventory/queries'
import type {
  PaymentListParams,
  PaymentSummaryParams,
  PurchaseListParams,
  PurchaseSummaryParams,
  ReturnListParams,
  ReturnSummaryParams,
} from './search'

/** Every purchasing query lives under ['purchasing']: any posting refreshes all of them at once. */
export const purchasingKeys = {
  all: ['purchasing'] as const,
  purchases: ['purchasing', 'purchases'] as const,
  purchaseList: (query: PurchaseListParams) => [...purchasingKeys.purchases, 'list', query] as const,
  purchaseSummary: (query: PurchaseSummaryParams) => [...purchasingKeys.purchases, 'summary', query] as const,
  duplicates: (supplierId: string, reference: string, excludeId: string | undefined) =>
    [...purchasingKeys.purchases, 'duplicates', supplierId, reference, excludeId ?? null] as const,
  purchase: (id: string) => [...purchasingKeys.purchases, 'detail', id] as const,
  openPurchases: (supplierId: string) => [...purchasingKeys.purchases, 'open', supplierId] as const,
  returnable: (supplierId: string, q = '') =>
    [...purchasingKeys.purchases, 'returnable', supplierId, q] as const,
  payments: ['purchasing', 'payments'] as const,
  paymentList: (query: PaymentListParams) => [...purchasingKeys.payments, 'list', query] as const,
  paymentSummary: (query: PaymentSummaryParams) => [...purchasingKeys.payments, 'summary', query] as const,
  payment: (id: string) => [...purchasingKeys.payments, 'detail', id] as const,
  returns: ['purchasing', 'returns'] as const,
  returnList: (query: ReturnListParams) => [...purchasingKeys.returns, 'list', query] as const,
  returnSummary: (query: ReturnSummaryParams) => [...purchasingKeys.returns, 'summary', query] as const,
  supplierReturn: (id: string) => [...purchasingKeys.returns, 'detail', id] as const,
  statement: (supplierId: string, from: string, to: string) =>
    ['purchasing', 'statement', supplierId, from, to] as const,
  aging: (asOf: string) => ['purchasing', 'aging', asOf] as const,
  supplierAging: (supplierId: string, asOf: string) => ['purchasing', 'aging', asOf, supplierId] as const,
}

/** The largest page the API serves: enough open purchases for one supplier's allocation. */
export const OPEN_PURCHASES_LIMIT = 200

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

export type SupplierPaymentListItem = Omit<SupplierPayment, 'allocations'>

// ── purchases ────────────────────────────────────────────────────────────────

/** The list's filters, view and sort (without paging): what the list, its export and its tabs share. */
function purchaseFilters(query: PurchaseListParams) {
  return {
    sort: query.sort,
    q: query.q,
    status: query.status,
    open: query.open,
    paymentStatus: query.paymentStatus,
    supplierId: query.supplierId,
    orgUnitId: query.orgUnitId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

export function purchaseListQueryOptions(query: PurchaseListParams) {
  return queryOptions({
    queryKey: purchasingKeys.purchaseList(query),
    queryFn: ({ signal }) =>
      api.get<Page<PurchaseListItem>>('/purchasing/purchases', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...purchaseFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per view tab for the list's filters (`GET /purchasing/purchases/summary`). */
export function purchaseSummaryQueryOptions(query: PurchaseSummaryParams) {
  return queryOptions({
    queryKey: purchasingKeys.purchaseSummary(query),
    queryFn: ({ signal }) => api.get<PurchaseSummary>('/purchasing/purchases/summary', { signal, query }),
    placeholderData: keepPreviousData,
  })
}

/** Every purchase of the view (not only this page), as CSV with headers in the UI language. */
export function exportPurchases(query: PurchaseListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/purchasing/purchases/export', {
    query: { format: 'csv', lang: language, ...purchaseFilters(query) },
    fallbackName: 'purchases.csv',
  })
}

/**
 * Other purchases of the supplier carrying the same supplier's reference (the duplicate-bill check,
 * PO-H4, Q15): a warning, never a block. Void purchases are not counted.
 */
export function purchaseDuplicatesQueryOptions(
  supplierId: string,
  supplierReference: string,
  excludeId?: string | undefined,
) {
  return queryOptions({
    queryKey: purchasingKeys.duplicates(supplierId, supplierReference, excludeId),
    queryFn: ({ signal }) =>
      api.get<PurchaseDuplicate[]>('/purchasing/purchases/duplicates', {
        signal,
        query: { supplierId, supplierReference, excludeId },
      }),
    staleTime: 30_000,
  })
}

export function purchaseQueryOptions(id: string) {
  return queryOptions({
    queryKey: purchasingKeys.purchase(id),
    queryFn: ({ signal }) => api.get<Purchase>(path('/purchasing/purchases', id), { signal }),
  })
}

/** A supplier's posted purchases with an open balance, oldest first. */
export function openPurchasesQueryOptions(supplierId: string) {
  return queryOptions({
    queryKey: purchasingKeys.openPurchases(supplierId),
    queryFn: ({ signal }) =>
      api.get<Page<PurchaseListItem>>('/purchasing/purchases', {
        signal,
        query: { open: true, supplierId, pageSize: OPEN_PURCHASES_LIMIT, sort: 'purchaseDate' },
      }),
  })
}

/**
 * A supplier's posted purchases a return or allowance can be issued against, newest first: the 50 newest,
 * or those matching `q` (number, supplier reference) searched on the server, so older ones can be found.
 */
export function returnablePurchasesQueryOptions(supplierId: string, q = '') {
  const search = q.trim()
  return queryOptions({
    queryKey: purchasingKeys.returnable(supplierId, search),
    queryFn: ({ signal }) =>
      api.get<Page<PurchaseListItem>>('/purchasing/purchases', {
        signal,
        query: {
          status: 'POSTED',
          supplierId,
          pageSize: 50,
          sort: '-purchaseDate',
          ...(search ? { q: search } : {}),
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/**
 * Anything a purchasing document does can change lists, balances, statements and aging: refresh
 * them all (the supplier list's balances and the profile's figures live under the same key).
 * Posting, voiding and payments also move the books, cash and stock.
 */
export function invalidatePurchasing(queryClient: QueryClient, options: { ledger?: boolean } = {}) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: purchasingKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['suppliers', 'list'] }),
    options.ledger ? invalidateLedger(queryClient) : undefined,
    options.ledger ? queryClient.invalidateQueries({ queryKey: inventoryKeys.stock }) : undefined,
    options.ledger ? queryClient.invalidateQueries({ queryKey: inventoryKeys.movements }) : undefined,
  ])
}

function useDocumentMutation<TInput, TResult extends { id: string }>(
  request: (input: TInput) => Promise<TResult>,
  detailKey: (id: string) => readonly unknown[],
  options: { ledger?: boolean } = {},
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (doc) => {
      queryClient.setQueryData(detailKey(doc.id), doc)
      return invalidatePurchasing(queryClient, options)
    },
  })
}

function useDeleteDocument(url: string, detailKey: readonly unknown[]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(url),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: detailKey })
      return invalidatePurchasing(queryClient)
    },
  })
}

export function useSavePurchase(id: string | null) {
  return useDocumentMutation(
    (input: PurchaseInput | PurchaseUpdate) =>
      id
        ? api.put<Purchase>(path('/purchasing/purchases', id), input)
        : api.post<Purchase>('/purchasing/purchases', input),
    purchasingKeys.purchase,
  )
}

/** Post any draft purchase (the detail page, and Save and post from the editor). */
export function usePostPurchase() {
  return useDocumentMutation(
    ({ id, version }: { id: string; version: number }) =>
      api.post<Purchase>(path('/purchasing/purchases', id, 'post'), { version }),
    purchasingKeys.purchase,
    { ledger: true },
  )
}

/**
 * Submit a draft for approval (ADR-018): the purchase comes back locked, with its approval run.
 * The approvals inbox and badge (under ['approvals']) are refreshed too.
 */
export function useSubmitPurchase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      api.post<Purchase>(path('/purchasing/purchases', id, 'submit'), { version }),
    onSuccess: (doc) => {
      queryClient.setQueryData(purchasingKeys.purchase(doc.id), doc)
      return Promise.all([
        invalidatePurchasing(queryClient),
        queryClient.invalidateQueries({ queryKey: ['approvals'] }),
      ])
    },
  })
}

export function useVoidPurchase(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<Purchase>(path('/purchasing/purchases', id, 'void'), { reason }),
    purchasingKeys.purchase,
    { ledger: true },
  )
}

export function useDeletePurchase(id: string) {
  return useDeleteDocument(path('/purchasing/purchases', id), purchasingKeys.purchase(id))
}

// ── supplier payments ────────────────────────────────────────────────────────

function paymentFilters(query: PaymentListParams) {
  return {
    sort: query.sort,
    q: query.q,
    status: query.status,
    hasOpenCredit: query.hasOpenCredit,
    supplierId: query.supplierId,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    from: query.from,
    to: query.to,
  }
}

export function paymentListQueryOptions(query: PaymentListParams) {
  return queryOptions({
    queryKey: purchasingKeys.paymentList(query),
    queryFn: ({ signal }) =>
      api.get<Page<SupplierPaymentListItem>>('/purchasing/payments', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...paymentFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

export function paymentSummaryQueryOptions(query: PaymentSummaryParams) {
  return queryOptions({
    queryKey: purchasingKeys.paymentSummary(query),
    queryFn: ({ signal }) =>
      api.get<SupplierPaymentSummary>('/purchasing/payments/summary', { signal, query }),
    placeholderData: keepPreviousData,
  })
}

export function exportPayments(query: PaymentListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/purchasing/payments/export', {
    query: { format: 'csv', lang: language, ...paymentFilters(query) },
    fallbackName: 'supplier-payments.csv',
  })
}

export function paymentQueryOptions(id: string) {
  return queryOptions({
    queryKey: purchasingKeys.payment(id),
    queryFn: ({ signal }) => api.get<SupplierPayment>(path('/purchasing/payments', id), { signal }),
  })
}

export function useCreatePayment() {
  return useDocumentMutation(
    (input: SupplierPaymentInput) => api.post<SupplierPayment>('/purchasing/payments', input),
    purchasingKeys.payment,
    { ledger: true },
  )
}

export function useAllocatePayment(id: string) {
  return useDocumentMutation(
    (input: ApAllocateInput) =>
      api.post<SupplierPayment>(path('/purchasing/payments', id, 'allocations'), input),
    purchasingKeys.payment,
  )
}

export function useUnallocatePayment(id: string) {
  return useDocumentMutation(
    (allocationId: string) =>
      api.delete<SupplierPayment>(
        `${path('/purchasing/payments', id, 'allocations')}/${encodeURIComponent(allocationId)}`,
      ),
    purchasingKeys.payment,
  )
}

export function useVoidPayment(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<SupplierPayment>(path('/purchasing/payments', id, 'void'), { reason }),
    purchasingKeys.payment,
    { ledger: true },
  )
}

// ── supplier returns and allowances ──────────────────────────────────────────

function returnFilters(query: ReturnListParams) {
  return {
    sort: query.sort,
    q: query.q,
    kind: query.kind,
    status: query.status,
    hasOpenCredit: query.hasOpenCredit,
    supplierId: query.supplierId,
    purchaseId: query.purchaseId,
    from: query.from,
    to: query.to,
  }
}

export function returnListQueryOptions(query: ReturnListParams) {
  return queryOptions({
    queryKey: purchasingKeys.returnList(query),
    queryFn: ({ signal }) =>
      api.get<Page<SupplierReturnListItem>>('/purchasing/returns', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...returnFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

export function returnSummaryQueryOptions(query: ReturnSummaryParams) {
  return queryOptions({
    queryKey: purchasingKeys.returnSummary(query),
    queryFn: ({ signal }) => api.get<SupplierReturnSummary>('/purchasing/returns/summary', { signal, query }),
    placeholderData: keepPreviousData,
  })
}

export function exportReturns(query: ReturnListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/purchasing/returns/export', {
    query: { format: 'csv', lang: language, ...returnFilters(query) },
    fallbackName: 'supplier-returns.csv',
  })
}

export function supplierReturnQueryOptions(id: string) {
  return queryOptions({
    queryKey: purchasingKeys.supplierReturn(id),
    queryFn: ({ signal }) => api.get<SupplierReturn>(path('/purchasing/returns', id), { signal }),
  })
}

export function useSaveSupplierReturn(id: string | null) {
  return useDocumentMutation(
    (input: SupplierReturnInput | SupplierReturnUpdate) =>
      id
        ? api.put<SupplierReturn>(path('/purchasing/returns', id), input)
        : api.post<SupplierReturn>('/purchasing/returns', input),
    purchasingKeys.supplierReturn,
  )
}

/** Post any draft return or allowance (the detail page, and Save and post from the editor). */
export function usePostSupplierReturn() {
  return useDocumentMutation(
    ({ id, version }: { id: string; version: number }) =>
      api.post<SupplierReturn>(path('/purchasing/returns', id, 'post'), { version }),
    purchasingKeys.supplierReturn,
    { ledger: true },
  )
}

export function useVoidSupplierReturn(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<SupplierReturn>(path('/purchasing/returns', id, 'void'), { reason }),
    purchasingKeys.supplierReturn,
    { ledger: true },
  )
}

export function useDeleteSupplierReturn(id: string) {
  return useDeleteDocument(path('/purchasing/returns', id), purchasingKeys.supplierReturn(id))
}

export function useAllocateSupplierReturn(id: string) {
  return useDocumentMutation(
    (input: ApAllocateInput) =>
      api.post<SupplierReturn>(path('/purchasing/returns', id, 'allocations'), input),
    purchasingKeys.supplierReturn,
  )
}

export function useUnallocateSupplierReturn(id: string) {
  return useDocumentMutation(
    (allocationId: string) =>
      api.delete<SupplierReturn>(
        `${path('/purchasing/returns', id, 'allocations')}/${encodeURIComponent(allocationId)}`,
      ),
    purchasingKeys.supplierReturn,
  )
}

// ── payables reports ─────────────────────────────────────────────────────────

export function supplierStatementQueryOptions(supplierId: string, from: string, to: string) {
  return queryOptions({
    queryKey: purchasingKeys.statement(supplierId, from, to),
    queryFn: ({ signal }) =>
      api.get<SupplierStatement>(path('/suppliers', supplierId, 'statement'), {
        signal,
        query: { from, to },
      }),
    placeholderData: keepPreviousData,
  })
}

export function payablesAgingQueryOptions(asOf: string) {
  return queryOptions({
    queryKey: purchasingKeys.aging(asOf),
    queryFn: ({ signal }) => api.get<ApAgingRow[]>('/purchasing/aging', { signal, query: { asOf } }),
    placeholderData: keepPreviousData,
  })
}

/** The supplier's row of the payables aging as of `asOf` (null: nothing owed and no credit held). */
export function supplierAgingQueryOptions(supplierId: string, asOf: string) {
  return queryOptions({
    queryKey: purchasingKeys.supplierAging(supplierId, asOf),
    queryFn: async ({ signal }): Promise<ApAgingRow | null> => {
      const rows = await api.get<ApAgingRow[]>('/purchasing/aging', { signal, query: { asOf, supplierId } })
      return rows.find((row) => row.supplier.id === supplierId) ?? null
    },
  })
}
